import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState, type ReactElement } from 'react';
import { Title } from 'react-admin';

import { useHttpClient } from '@/app/providers';
import { discoverHydraResources, type HydraDiscoveryResult } from '@/discovery/hydraDiscovery';
import { GROUP_LABELS, RESOURCE_CATALOG } from '@/resources/catalog';
import { isBabyScoped, type ResourceDescriptor } from '@/resources/descriptors';

import { useBreadcrumbLeaf } from './shell/Breadcrumb';

/**
 * Diagnostic technique — ouvert depuis le menu utilisateur, hors de la barre
 * laterale : c'est un outil de developpeur, pas un objet d'administration.
 *
 * Cet ecran existait sous le nom de tableau de bord : il repondait a une question
 * de developpeur — « qu'est-ce que l'introspection Hydra decouvre ? » — posee a
 * un exploitant qui n'a aucune raison de savoir ce qu'est Hydra. Il n'a pas
 * disparu pour autant : la reponse reste utile le jour ou une classe
 * `#[ApiResource]` apparaitra cote serveur, et il faut alors pouvoir le
 * constater sans lire le code. Il a seulement quitte la page d'accueil.
 *
 * Le mecanisme de detection lui-meme n'est pas touche
 * (`src/discovery/hydraDiscovery.ts`) : seul son point de montage a bouge.
 */

const OPERATION_LABELS: Readonly<Record<string, string>> = {
  list: 'Liste',
  show: 'Fiche',
  create: 'Création',
  edit: 'Modification',
};

const operationChips = (descriptor: ResourceDescriptor): ReactElement[] =>
  Object.entries(descriptor.capabilities)
    .filter(([, enabled]) => enabled)
    .map(([operation]) => (
      <Chip key={operation} size="small" label={OPERATION_LABELS[operation] ?? operation} />
    ));

const HydraDiscoveryCard = ({ result }: { result: HydraDiscoveryResult | null }): ReactElement => {
  if (result === null) {
    return (
      <Card>
        <CardContent>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Interrogation de la documentation API Platform…</Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  // L'entrypoint coupe est l'etat VOULU du backend (`enable_entrypoint: false`) :
  // l'afficher en avertissement envoyait chercher une panne inexistante. Seul un
  // transport mort est une erreur ; une documentation illisible est un avertissement.
  const severity =
    result.status === 'resources-found'
      ? 'success'
      : result.status === 'unreachable'
        ? 'error'
        : result.status === 'documentation-error'
          ? 'warning'
          : 'info';

  return (
    <Card>
      <CardContent>
        <Stack spacing={1.5}>
          <Typography variant="h6">Détection automatique (API Platform / Hydra)</Typography>
          <Alert severity={severity} variant="outlined">
            {result.detail}
          </Alert>

          {result.resources.length > 0 ? (
            <Stack spacing={1}>
              {result.resources.map((resource) => (
                <Stack
                  key={resource.name}
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                >
                  <Chip size="small" color="success" label={resource.name} />
                  <Typography variant="body2" color="text.secondary">
                    {resource.fieldCount} champ(s) ·{' '}
                    {resource.operations.join(', ') || 'aucune opération décrite'}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};

/**
 * Description technique du catalogue declaratif.
 *
 * Elle a sa place ici et non sur la vue d'ensemble : « pagination serveur »,
 * « portee par `{babyId}` » et la liste des operations REST sont le vocabulaire
 * de qui branche l'outil, pas de qui l'utilise.
 */
const CatalogCard = (): ReactElement => (
  <Card>
    <CardContent>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">Catalogue déclaratif</Typography>
          <Typography variant="body2" color="text.secondary">
            Aligné à la main sur les contrôleurs réels de l’API, faute de description Hydra. Les
            opérations listées sont celles que le serveur expose ; les autres ne sont proposées
            nulle part dans l’interface.
          </Typography>
        </Box>

        {Object.keys(GROUP_LABELS).map((group) => {
          const resources = RESOURCE_CATALOG.filter((descriptor) => descriptor.group === group);

          if (resources.length === 0) {
            return null;
          }

          return (
            <Box key={group}>
              <Typography variant="overline" sx={{ fontWeight: 700, opacity: 0.75 }}>
                {GROUP_LABELS[group] ?? group}
              </Typography>
              <Stack divider={<Divider flexItem />} spacing={0}>
                {resources.map((descriptor) => (
                  <Stack
                    key={descriptor.name}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ alignItems: { xs: 'flex-start', sm: 'center' }, py: 1 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 200 }}>
                      {descriptor.label}
                    </Typography>
                    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                      {operationChips(descriptor)}
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          descriptor.pagination === 'server'
                            ? 'Pagination serveur'
                            : 'Pagination client'
                        }
                      />
                      {isBabyScoped(descriptor) ? (
                        <Chip size="small" variant="outlined" label="Portée par un profil bébé" />
                      ) : (
                        <Chip size="small" variant="outlined" label="Ressource globale" />
                      )}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </CardContent>
  </Card>
);

export const DiagnosticPage = (): ReactElement => {
  const http = useHttpClient();

  useBreadcrumbLeaf('Diagnostic technique');
  const [discovery, setDiscovery] = useState<HydraDiscoveryResult | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async (): Promise<void> => {
      const result = await discoverHydraResources({
        apiUrl: http.apiUrl,
        authorizationHeader: (): Record<string, string> => {
          const session = http.session();

          // La documentation Hydra est publique sur ce backend, mais la poser
          // quand la session existe evite de dependre de ce detail : si les
          // regles d'acces changent, la detection continuera de fonctionner.
          return session === null ? {} : { Authorization: `Bearer ${session.tokens.accessToken}` };
        },
      });

      // Sans ce garde, un demontage pendant l'appel provoquerait une mise a jour
      // d'etat sur un composant retire.
      if (!cancelled) {
        setDiscovery(result);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [http]);

  return (
    <>
      <Title title="Diagnostic technique" />
      <Stack spacing={2} sx={{ mt: 2 }}>
        <Card>
          <CardContent>
            <Stack spacing={1}>
              <Typography variant="h6">Diagnostic technique</Typography>
              <Typography variant="body2" color="text.secondary">
                Cette page s’adresse à qui installe ou dépanne le back-office. Elle n’est pas
                nécessaire à son usage courant.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                API interrogée : <code>{http.apiUrl}</code>
              </Typography>
            </Stack>
          </CardContent>
        </Card>
        <HydraDiscoveryCard result={discovery} />
        <CatalogCard />
      </Stack>
    </>
  );
};
